/**
 * Bundle Builder - Main Application (ADA / WCAG 2.1 AA Accessible)
 *
 * Accessibility fixes applied:
 * - Modal: focus trap, focus restoration, Escape key handling
 * - Product cards: keyboard-focusable image wrappers with role="button"
 * - Quantity buttons: aria-label with product name context
 * - Progress bars: dynamic aria-valuenow / aria-valuetext updates
 * - Live region announcements on add/remove/total changes
 * - Toast: role="alert" + aria-live="assertive"
 * - Step navigation: focus moves to step title on prev/next
 * - Mobile expand/collapse: aria-expanded toggling
 * - Compare-at price: accessible contrast color (#767676)
 * - Modal image: dynamic alt text
 */

if (!window.BundleBuilder) {
    window.BundleBuilder = class BundleBuilder {
    constructor(config, productData) {
        this.config = config;
        this.productData = productData;
        this.currentStepIndex = 0;
        this.selections = {};
        this.totalQuantity = 0;
        this.subtotal = 0;
        this.discount = 0;
        this.total = 0;
        this._maxToastTimer = null;
        this._modalTrigger = null;   // ADA: track element that opened modal
        this._trapFocusHandler = null; // ADA: focus trap handler reference
        
        this.init();
    }
    
    init() {
        this.renderProgressSteps();
        this.renderCurrentStep();
        this.bindEvents();
        this.updateSummary();
        this.initializeMobileSummary();
        this.forceOverflowVisible();
    }
    
    forceOverflowVisible() {
        setTimeout(() => {
            const selectors = [
                '.bundle-builder',
                '.bundle-builder-main',
                '.bundle-builder-content',
                '#productGrid',
                '.product-grid',
                '.step-container'
            ];
            selectors.forEach(selector => {
                document.querySelectorAll(selector).forEach(el => {
                    el.style.overflow = 'visible';
                    el.style.setProperty('overflow', 'visible', 'important');
                });
            });
        }, 100);
    }
    
    // ──────────────────────────────────────────────
    // ADA: Announce message to screen readers via live region
    // ──────────────────────────────────────────────
    announce(message) {
        const liveRegion = document.getElementById('bundleLiveRegion');
        if (liveRegion) {
            // Clear then set to ensure re-announcement of identical text
            liveRegion.textContent = '';
            requestAnimationFrame(() => {
                liveRegion.textContent = message;
            });
        }
    }

    renderProgressSteps() {
        const progressContainer = document.getElementById('progressSteps');
        progressContainer.innerHTML = '';
        
        const progressWrapper = progressContainer.closest('.builder-progress');
        if (this.config.steps.length === 1) {
            if (progressWrapper) progressWrapper.style.display = 'none';
            return;
        }
        if (progressWrapper) progressWrapper.style.display = '';

        const totalSteps = this.config.steps.length;
        
        // Calculate fill percentage for the connecting line
        if (totalSteps > 1) {
            requestAnimationFrame(() => {
                const steps = progressContainer.querySelectorAll('.progress-step');
                const containerRect = progressContainer.getBoundingClientRect();
                const firstCircle = steps[0].querySelector('.step-number');
                const lastCircle = steps[steps.length - 1].querySelector('.step-number');
                const isMobile = window.innerWidth <= 768;

                if (isMobile) {
                    const firstCenter = firstCircle.getBoundingClientRect().top + (firstCircle.offsetHeight / 2) - containerRect.top;
                    const lastCenter = lastCircle.getBoundingClientRect().top + (lastCircle.offsetHeight / 2) - containerRect.top;

                    progressContainer.style.setProperty('--line-top', `${firstCenter}px`);
                    progressContainer.style.setProperty('--line-height', `${lastCenter - firstCenter}px`);
                    progressContainer.style.setProperty('--line-left-mobile', `${firstCircle.getBoundingClientRect().left + (firstCircle.offsetWidth / 2) - containerRect.left}px`);

                    if (this.currentStepIndex > 0) {
                        const currentCircle = steps[this.currentStepIndex].querySelector('.step-number');
                        const currentCenter = currentCircle.getBoundingClientRect().top + (currentCircle.offsetHeight / 2) - containerRect.top;
                        progressContainer.style.setProperty('--progress-fill-mobile', `${currentCenter - firstCenter}px`);
                    } else {
                        progressContainer.style.setProperty('--progress-fill-mobile', '0px');
                    }
                } else {
                    const firstCenter = firstCircle.getBoundingClientRect().left + (firstCircle.offsetWidth / 2) - containerRect.left;
                    const lastCenter = lastCircle.getBoundingClientRect().left + (lastCircle.offsetWidth / 2) - containerRect.left;

                    progressContainer.style.setProperty('--line-left', `${firstCenter}px`);
                    progressContainer.style.setProperty('--line-width', `${lastCenter - firstCenter}px`);

                    if (this.currentStepIndex > 0) {
                        const currentCircle = steps[this.currentStepIndex].querySelector('.step-number');
                        const currentCenter = currentCircle.getBoundingClientRect().left + (currentCircle.offsetWidth / 2) - containerRect.left;
                        progressContainer.style.setProperty('--progress-fill', `${currentCenter - firstCenter}px`);
                    } else {
                        progressContainer.style.setProperty('--progress-fill', '0px');
                    }
                }
            });
        }
        
        this.config.steps.forEach((step, index) => {
            const stepEl = document.createElement('div');
            stepEl.className = 'progress-step';
            if (index === this.currentStepIndex) stepEl.classList.add('active');
            if (index < this.currentStepIndex) stepEl.classList.add('completed');

            if (index === this.currentStepIndex) {
                stepEl.setAttribute('aria-current', 'step');
            }

            const safeDescription = step.description || '';

            stepEl.innerHTML = `
                <div class="step-number" aria-hidden="true">${index + 1}</div>
                <div class="step-text-wrapper">
                    <div class="step-label">
                        <span class="sr-only">Step ${index + 1}:</span>
                        ${step.title}
                        ${index < this.currentStepIndex ? '<span class="sr-only">(completed)</span>' : ''}
                        ${index === this.currentStepIndex ? '<span class="sr-only">(current)</span>' : ''}
                    </div>
                    ${safeDescription ? `<div class="step-description-text">${safeDescription}</div>` : ''}
                </div>
            `;
            progressContainer.appendChild(stepEl);
        });
    }
    
    renderCurrentStep() {
        const step = this.config.steps[this.currentStepIndex];
        const products = this.productData[step.productHandle];
        
        const stepTitleEl = document.getElementById('stepTitle');
        const stepNumber = this.currentStepIndex + 1;
        const longTitle = step.titleLong || step.title;
        if (longTitle) {
            /*stepTitleEl.textContent = `${stepNumber}.  ${longTitle}`;*/
            stepTitleEl.textContent = `${longTitle}`;
            stepTitleEl.style.display = '';
        } else {
            stepTitleEl.textContent = '';
            stepTitleEl.style.display = 'none';
        }
        // Description only shows in progress steps, not here
        document.getElementById('stepDescription').textContent = '';
        this.updateStepCounter();
        
        const grid = document.getElementById('productGrid');
        grid.innerHTML = '';
        
        if (!products) {
            grid.innerHTML = `
                <div style="padding: 2rem; text-align: center; background: #f8f8f8; border-radius: 8px; grid-column: 1 / -1;" role="alert">
                    <h3 style="margin-bottom: 1rem;">Products Not Configured</h3>
                    <p style="color: #666; margin-bottom: 0.5rem;">No products found for handle: <strong>${step.productHandle}</strong></p>
                    <p style="color: #767676; font-size: 0.875rem;">Please check your configuration and ensure products are set up correctly.</p>
                </div>
            `;
            return;
        }
        
        if (!products.variants || products.variants.length === 0) {
            grid.innerHTML = `
                <div style="padding: 2rem; text-align: center; background: #f8f8f8; border-radius: 8px; grid-column: 1 / -1;" role="alert">
                    <h3 style="margin-bottom: 1rem;">No Variants Found</h3>
                    <p style="color: #666; margin-bottom: 0.5rem;">Product <strong>${products.title || step.productHandle}</strong> has no variants.</p>
                    <p style="color: #767676; font-size: 0.875rem;">Add variants to this product in Shopify admin.</p>
                </div>
            `;
            return;
        }
        
        if (!this.selections[step.id]) {
            this.selections[step.id] = {};
        }
        
        if (step.preselected && step.preselected.length > 0) {
            step.preselected.forEach(variantId => {
                const variant = products.variants.find(v => v.variantId === variantId);
                if (variant && variant.available && !this.selections[step.id][variantId]) {
                    this.selections[step.id][variantId] = 1;
                }
            });
        }
        
        if (products.variants.length === 1 && step.maxSelections === 1) {
            const singleVariant = products.variants[0];
            if (singleVariant.available && !this.selections[step.id][singleVariant.variantId]) {
                this.selections[step.id][singleVariant.variantId] = 1;
            }
        }
        
        const visibleVariants = this.config.hideOutOfStock
        ? products.variants.filter(v => v.available)
        : products.variants;

        visibleVariants.forEach(variant => {
            const isPreselected = step.preselected && step.preselected.includes(variant.variantId);
            const quantity = this.selections[step.id][variant.variantId] || 0;
            const isSelected = quantity > 0;
            
            const card = document.createElement('div');
            card.className = 'product-card';
            if (isSelected) card.classList.add('selected');
            if (isPreselected && !step.showPreselected) card.classList.add('preselected');
            if (!variant.available) card.classList.add('out-of-stock');
            
            // ADA: Escape HTML for use in aria-label attributes
            const safeTitle = variant.title.replace(/"/g, '&quot;');

            card.innerHTML = `
                <div class="product-image-wrapper"
                     tabindex="0"
                     role="button"
                     aria-label="View details for ${safeTitle}">
                    <img src="${variant.image}" alt="${variant.title}" class="product-image">
                    ${isSelected ? `<span class="quantity-badge" aria-hidden="true">${quantity}</span>` : ''}
                    ${isSelected ? `<span class="selected-checkmark" aria-hidden="true">✓</span>` : ''}
                </div>
                <div class="product-info">
                    <h4 class="product-title">${variant.title}</h4>
                </div>
                ${!this.config.hideVariantPricesMobile ? `
                    <div class="product-price">
                        ${variant.compareAtPrice ?
                            `<span class="compare-at-price"><span class="sr-only">Original price:</span>$${variant.compareAtPrice.toFixed(2)}</span>` :
                            ''}
                        <span${variant.compareAtPrice ? ' class="sale-price"' : ''}>
                            ${variant.compareAtPrice ? '<span class="sr-only">Sale price:</span>' : ''}
                            $${variant.price.toFixed(2)}
                        </span>
                    </div>
                ` : ''}
                <div class="desktop-quantity-controls">
                    ${isSelected ? `
                        <div class="quantity-selector" role="group" aria-label="Quantity for ${safeTitle}">
                            <button class="qty-btn btn btn-mini text-center" data-variant-id="${variant.variantId}"
                                    aria-label="Decrease quantity of ${safeTitle}">−</button>
                            <div class="qty-display" aria-live="polite">
                                <span class="qty-label" aria-hidden="true">QTY:</span>
                                <span class="qty-number">${quantity}</span>
                                <span class="sr-only">Quantity: ${quantity}</span>
                            </div>
                            <button class="qty-btn btn btn-mini text-center" data-variant-id="${variant.variantId}"
                                    aria-label="Increase quantity of ${safeTitle}">+</button>
                        </div>
                    ` : `
                        <button class="add-to-bundle-btn btn btn-primary"
                                data-variant-id="${variant.variantId}"
                                ${!variant.available ? 'disabled' : ''}
                                aria-label="${variant.available ? 'Add ' + safeTitle + ' to bundle' : safeTitle + ' is out of stock'}">
                            ${variant.available ? 'Add to Box' : 'Out of Stock'}
                        </button>
                    `}
                </div>

                <button class="product-add-btn btn btn-primary"
                        data-variant-id="${variant.variantId}"
                        data-mobile-add
                        ${!variant.available ? 'disabled' : ''}
                        aria-label="${variant.available ? 'Add ' + safeTitle + ' to bundle' : safeTitle + ' is out of stock'}"
                        style="display: ${isSelected ? 'none' : 'block'}">
                    ${variant.available ? 'Add to Box' : 'Out of Stock'}
                </button>
                <div class="mobile-quantity-controls ${isSelected ? 'active' : ''}"
                     data-variant-id="${variant.variantId}"
                     role="group" aria-label="Quantity for ${safeTitle}">
                    <button class="product-qty-btn" data-action="minus"
                            aria-label="Decrease quantity of ${safeTitle}">−</button>
                    <div class="product-qty-display" aria-live="polite">
                        <span class="qty-label" aria-hidden="true">QTY:</span>
                        <span class="qty-number">${quantity}</span>
                        <span class="sr-only">Quantity: ${quantity}</span>
                    </div>
                    <button class="product-qty-btn" data-action="plus"
                            aria-label="Increase quantity of ${safeTitle}">+</button>
                </div>
            `;
            
            // ADA: Image wrapper — click AND keyboard handler
            const imgWrapper = card.querySelector('.product-image-wrapper');
            imgWrapper.addEventListener('click', () => {
                this.openProductModal(variant, step.id);
            });
            imgWrapper.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.openProductModal(variant, step.id);
                }
            });
            
            card.querySelector('.product-title').addEventListener('click', () => {
                this.openProductModal(variant, step.id);
            });
            
            if (isSelected) {
                const buttons = card.querySelectorAll('.qty-btn');
                buttons[0].addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.updateQuantity(step.id, variant.variantId, -1);
                });
                buttons[1].addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.updateQuantity(step.id, variant.variantId, 1);
                });
            } else {
                const addBtn = card.querySelector('.add-to-bundle-btn');
                if (addBtn) {
                    addBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        this.updateQuantity(step.id, variant.variantId, 1);
                    });
                }
            }
            
            grid.appendChild(card);
        });
        this.renderFilters(step, products);
        this.updateNavigationButtons();
    }
    
    openProductModal(variant, stepId) {
        const modal = document.getElementById('productModal');
        const quantity = this.selections[stepId][variant.variantId] || 0;
        const isInBundle = quantity > 0;
        // ADA: Update modal image alt text dynamically
        const modalImg = document.getElementById('modalImage');
        modalImg.src = variant.image;
        modalImg.alt = variant.title;
        document.getElementById('modalTitle').textContent = variant.title;
        const modalPriceEl = document.getElementById('modalPrice');
        if (this.config.hideVariantPricesMobile) {
            modalPriceEl.style.display = 'none';
        } else {
            modalPriceEl.style.display = 'block';
            modalPriceEl.textContent = `$${variant.price.toFixed(2)}`;
        }
        document.getElementById('modalDescription').innerHTML = variant.description;
        
        const addBtn = document.getElementById('modalAddBtn');
        const closeBtn = document.getElementById('modalCloseBtn');

        let oosMsg = document.getElementById('modalOosMessage');
        if (!oosMsg) {
            oosMsg = document.createElement('p');
            oosMsg.id = 'modalOosMessage';
            oosMsg.style.cssText = 'color: var(--color-error); font-weight: 600; text-align: center; margin: 0;';
            addBtn.parentNode.insertBefore(oosMsg, addBtn.nextSibling);
        }

        if (!variant.available) {
            addBtn.style.display = 'none';
            addBtn.disabled = false;
            oosMsg.style.display = 'none';
            closeBtn.style.display = 'block';
        } else if (isInBundle) {
            addBtn.style.display = 'none';
            addBtn.disabled = false;
            oosMsg.style.display = 'none';
            closeBtn.style.display = 'block';
        } else {
            addBtn.style.display = 'block';
            addBtn.disabled = false;
            oosMsg.style.display = 'none';
            closeBtn.style.display = 'none';
        }
        
        modal.dataset.variantId = variant.variantId;
        modal.dataset.stepId = stepId;
        
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';

        // ── ADA: Save trigger, move focus into modal, set up focus trap ──
        this._modalTrigger = document.activeElement;

        // Small delay to let display:flex apply before focusing
        requestAnimationFrame(() => {
            const firstFocusable = modal.querySelector(
                'button:not([style*="display: none"]):not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) firstFocusable.focus();
        });

        // Focus trap
        this._trapFocusHandler = (e) => {
            if (e.key !== 'Tab') return;
            const focusableEls = modal.querySelectorAll(
                'button:not([style*="display: none"]):not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (focusableEls.length === 0) return;
            const first = focusableEls[0];
            const last = focusableEls[focusableEls.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', this._trapFocusHandler);
    }
    
    closeProductModal() {
        const modal = document.getElementById('productModal');
        modal.classList.remove('active');
        document.body.style.overflow = '';

        // ADA: Remove focus trap and restore focus to trigger element
        if (this._trapFocusHandler) {
            document.removeEventListener('keydown', this._trapFocusHandler);
            this._trapFocusHandler = null;
        }
        if (this._modalTrigger) {
            this._modalTrigger.focus();
            this._modalTrigger = null;
        }
    }
    
    updateQuantity(stepId, variantId, change) {
        const step = this.config.steps.find(s => s.id === stepId);
        const currentQty = this.selections[stepId][variantId] || 0;
        let newQty = currentQty + change;
        
        if (newQty < 0) newQty = 0;
        
        const currentStepTotal = Object.values(this.selections[stepId]).reduce((sum, qty) => sum + qty, 0);
        const newStepTotal = currentStepTotal - currentQty + newQty;
        
        if (step.maxSelections != null && step.maxSelections > 0 && newStepTotal > step.maxSelections) {
            this.showMaxToast();
            return;
        }

        // ADA: Find variant name for announcement
        const products = this.productData[step.productHandle];
        const variant = products?.variants.find(v => v.variantId == variantId);
        const variantName = variant ? variant.title : 'item';
        
        if (newQty === 0) {
            delete this.selections[stepId][variantId];
            this.announce(`${variantName} removed from bundle.`);
        } else {
            this.selections[stepId][variantId] = newQty;
            if (change > 0) {
                this.announce(`${variantName} added. Quantity: ${newQty}.`);
            } else {
                this.announce(`${variantName} quantity decreased to ${newQty}.`);
            }
        }
        
        this.renderCurrentStep();
        this.updateSummary();
        this.updateStepCounter();
    }

    showMaxToast() {
        let toast = document.getElementById('bundleMaxToast');
        if (!toast) {
            toast = document.createElement('div');
            toast.id = 'bundleMaxToast';
            toast.className = 'bundle-max-toast';
            // ADA: role="alert" + aria-live for screen reader announcement
            toast.setAttribute('role', 'alert');
            toast.setAttribute('aria-live', 'assertive');
            toast.textContent = 'You have already chosen the maximum number of items.';
            document.body.appendChild(toast);
        }

        if (this._maxToastTimer) {
            clearTimeout(this._maxToastTimer);
            toast.classList.remove('hiding');
        }

        requestAnimationFrame(() => toast.classList.add('visible'));

        this._maxToastTimer = setTimeout(() => {
            toast.classList.add('hiding');
            setTimeout(() => toast.classList.remove('visible', 'hiding'), 300);
        }, 2500);
    }
    
    updateStepCounter() {
        const counter = document.getElementById('stepCounter');
        counter.textContent = '';
        counter.className = 'step-counter';
    }
    
    updateSummary() {
        const summaryContainer = document.getElementById('summaryItems');
        summaryContainer.innerHTML = '';
        
        this.subtotal = 0;
        this.totalQuantity = 0;
        
        this.config.steps.forEach(step => {
            const products = this.productData[step.productHandle];
            if (!products || !this.selections[step.id]) return;
            
            Object.entries(this.selections[step.id]).forEach(([variantId, quantity]) => {
                const variant = products.variants.find(v => v.variantId == variantId);
                if (!variant) {
                    console.warn(`Variant ${variantId} not found in product ${step.productHandle}`);
                    return;
                }
                
                const itemTotal = variant.price * quantity;
                this.subtotal += itemTotal;
                this.totalQuantity += quantity;
                
                const itemEl = document.createElement('div');
                itemEl.className = 'summary-item';
                itemEl.innerHTML = `
                    <div class="summary-item-image">
                        <img src="${variant.image}" alt="${variant.title}">
                    </div>
                    <div class="summary-item-title">${variant.title}</div>
                    <div class="summary-item-meta">x${quantity}</div>
                    ${!this.config.hideVariantPricesMobile ? `<div class="summary-item-price">$${itemTotal.toFixed(2)}</div>` : ''}
                `;
                summaryContainer.appendChild(itemEl);
            });
        });
        
        if (this.config.pricingMode === 'fixed') {
            this.discount = 0;
            this.total = this.config.fixedPrice || 0;
        } else {
            let discountedTotalCents = 0;
            let totalDiscountCents = 0;

            this.config.steps.forEach(step => {
                const products = this.productData[step.productHandle];
                if (!products || !this.selections[step.id]) return;
                Object.entries(this.selections[step.id]).forEach(([variantId, quantity]) => {
                    const variant = products.variants.find(v => v.variantId == variantId);
                    if (!variant) return;
                    const lineCents = Math.round(variant.price * quantity * 100);
                    let lineDiscountCents = 0;

                    if (this.config.pricingMode === 'percentage') {
                        const tier = this.getAppliedDiscountTier();
                        const pct = tier ? tier.discount * 100 : (this.config.discountPercentage || 0);
                        // Match Shopify: floor per unit × quantity
                        const perUnitCents = Math.round(variant.price * 100);
                        const perUnitDiscount = Math.floor(perUnitCents * pct / 100);
                        lineDiscountCents = perUnitDiscount * quantity;
                    } else if (this.config.pricingMode === 'amount') {
                        const tier = this.getAppliedDiscountTier();
                        const amtCents = tier ? Math.round(tier.discount * 100) : Math.round(this.config.discountAmount || 0);
                        const subtotalCents = Math.round(this.subtotal * 100);
                        totalDiscountCents = Math.min(amtCents, subtotalCents);
                        discountedTotalCents = subtotalCents - totalDiscountCents;
                    }

                    totalDiscountCents += lineDiscountCents;
                    discountedTotalCents += lineCents - lineDiscountCents;
                });
            });

            // Clamp discount to intended amount to prevent rounding drift
            if (this.config.pricingMode === 'amount') {
                const tier = this.getAppliedDiscountTier();
                const intendedCents = tier ? Math.round(tier.discount * 100) : Math.round(this.config.discountAmount || 0);
                const subtotalCents = Math.round(this.subtotal * 100);
                totalDiscountCents = Math.min(totalDiscountCents, intendedCents, subtotalCents);
                discountedTotalCents = subtotalCents - totalDiscountCents;
            }
            this.discount = totalDiscountCents / 100;
            this.total = discountedTotalCents / 100;
        }
        
        document.getElementById('subtotal').textContent = `$${this.subtotal.toFixed(2)}`;
        document.getElementById('total').textContent = `$${this.total.toFixed(2)}`;
        /*
        document.getElementById('desktopBundleQuantity').textContent = `x${this.totalQuantity}`;
        document.getElementById('subtotalQuantity').textContent = `x${this.totalQuantity}`;
        */
        
        const subtotalRow = document.getElementById('subtotalRow');
        const summaryTotals = subtotalRow.parentElement;
        const hasDiscount = this.discount > 0 && this.config.pricingMode !== 'fixed';
        if (this.config.hideVariantPricesMobile || !hasDiscount) {
            subtotalRow.style.display = 'none';
            if (summaryTotals && summaryTotals.classList.contains('summary-totals')) {
                summaryTotals.style.borderTop = 'none';
                summaryTotals.style.paddingTop = '0';
                summaryTotals.style.marginTop = '0';
            }
        } else {
            subtotalRow.style.display = 'flex';
            if (summaryTotals && summaryTotals.classList.contains('summary-totals')) {
                summaryTotals.style.borderTop = '';
                summaryTotals.style.paddingTop = '';
                summaryTotals.style.marginTop = '';
            }
        }
        
        const discountRow = document.getElementById('discountRow');
        this.subtotal = Math.round(this.subtotal * 100) / 100;
        if (this.config.pricingMode === 'fixed') {
            discountRow.style.display = 'none';
        } else if (this.discount > 0) {
            const discountTier = this.getAppliedDiscountTier();
            document.getElementById('discountLabel').textContent =
                discountTier?.label || 'Discount';
            document.getElementById('discountAmount').textContent = `-$${this.discount.toFixed(2)}`;
            discountRow.style.display = 'flex';
        } else {
            discountRow.style.display = 'none';
        }
        

        // ── ADA: Update progress bar ARIA values ──
        const summaryProgressText = document.getElementById('summaryProgressText');
        const summaryProgress = document.getElementById('summaryProgress');
        const summaryProgressBar = document.getElementById('summaryProgressBar');
        
        if (summaryProgressText && summaryProgress) {
            const maxItems = this.config.pricingMode === 'fixed'
                ? this.config.requiredQuantity
                : this.config.steps.reduce((sum, step) => sum + (step.maxSelections || 12), 0) || 12;
            
            summaryProgressText.textContent = `${this.totalQuantity}/${maxItems} Items`;
            
            if (summaryProgressBar) {
                const progressPercent = Math.min((this.totalQuantity / maxItems) * 100, 100);
                summaryProgressBar.style.width = `${progressPercent}%`;
            }

            // ADA: Update progressbar ARIA
            const progressBarWrapper = document.getElementById('summaryProgressBarWrapper');
            if (progressBarWrapper) {
                progressBarWrapper.setAttribute('aria-valuenow', this.totalQuantity);
                progressBarWrapper.setAttribute('aria-valuemax', maxItems);
                progressBarWrapper.setAttribute('aria-valuetext',
                    `${this.totalQuantity} of ${maxItems} items selected`);
            }
            
            if (this.totalQuantity >= maxItems) {
                summaryProgress.classList.add('summary-progress-filled');
            } else {
                summaryProgress.classList.remove('summary-progress-filled');
            }
            // Hide progress bar if toggled off
            if (!this.config.showProgressBar) {
                summaryProgress.style.display = 'none';
            }
        }
        
        this.updateDiscountProgress();
        
        const allRequiredStepsComplete = this.validateAllRequiredSteps();
        const addToCartBtn = document.getElementById('addToCartBtn');
        addToCartBtn.disabled = this.totalQuantity === 0 || !allRequiredStepsComplete.isValid;
        addToCartBtn.title = allRequiredStepsComplete.isValid ? '' : allRequiredStepsComplete.message;
        
        this.updateMobileSummary();
    }
    
    updateDiscountProgress() {
        const progressContainer = document.getElementById('discountProgress');
        const progressLabel = document.getElementById('discountProgressLabel');
        const progressFill = document.getElementById('discountProgressFill');
        
        if (this.config.showDiscountProgress === false) {
            progressContainer.style.display = 'none';
            return;
        }

        if (this.config.pricingMode === 'fixed') {
            progressContainer.style.display = 'none';
            return;
        }
        
        if (!this.config.discountTiers || this.config.discountTiers.length === 0) {
            progressContainer.style.display = 'none';
            return;
        }
        
        const nextTier = this.getNextDiscountTier();
        const currentTier = this.getAppliedDiscountTier();
        
        /*if (nextTier) {
            const needed = nextTier.minQuantity - this.totalQuantity;
            const progress = (this.totalQuantity / nextTier.minQuantity) * 100;
            progressContainer.style.display = 'block';
            progressLabel.textContent = `Add ${needed} more item${needed !== 1 ? 's' : ''} to unlock${nextTier.label ? ' ' + nextTier.label : ' your discount'}!`;
            progressFill.style.width = `${Math.min(progress, 100)}%`;
        } else if (currentTier) {
            progressContainer.style.display = 'block';
            progressLabel.textContent = `Maximum discount unlocked${currentTier.label ? ': ' + currentTier.label : ''}!`;
            progressFill.style.width = '100%';
        } else {
            const firstTier = this.config.discountTiers[0];
            if (firstTier) {
                progressContainer.style.display = 'block';
                progressLabel.textContent = `Add ${firstTier.minQuantity} items to unlock${firstTier.label ? ' ' + firstTier.label : ' your discount'}!`;
                progressFill.style.width = '0%';
            } else {
                progressContainer.style.display = 'none';
            }
        }*/
        if (nextTier && currentTier) {
            const needed = nextTier.minQuantity - this.totalQuantity;
            const progress = (this.totalQuantity / nextTier.minQuantity) * 100;
            progressContainer.style.display = 'block';
            progressLabel.textContent = `Congrats! You've got ${currentTier.label || 'your discount'}. Add ${needed} more item${needed !== 1 ? 's' : ''} to unlock ${nextTier.label || 'a bigger discount'}!`;
            progressFill.style.width = `${Math.min(progress, 100)}%`;
        } else if (nextTier) {
            const needed = nextTier.minQuantity - this.totalQuantity;
            const progress = (this.totalQuantity / nextTier.minQuantity) * 100;
            progressContainer.style.display = 'block';
            progressLabel.textContent = `Add ${needed} more item${needed !== 1 ? 's' : ''} to unlock ${nextTier.label || 'your discount'}!`;
            progressFill.style.width = `${Math.min(progress, 100)}%`;
        } else if (currentTier) {
            progressContainer.style.display = 'block';
            progressLabel.textContent = `Congrats! Maximum discount unlocked: ${currentTier.label || 'your discount'}!`;
            progressFill.style.width = '100%';
        }
    }
    
    getNextDiscountTier() {
        if (!this.config.discountTiers || this.config.discountTiers.length === 0) return null;
        return this.config.discountTiers.find(tier => {
            if (tier.minQuantity) return this.totalQuantity < tier.minQuantity;
            if (tier.minPrice) return this.subtotal < tier.minPrice;
            return false;
        });
    }
    
    validateAllRequiredSteps() {
        const incompleteSteps = [];
        this.config.steps.forEach(step => {
            const stepTotal = Object.values(this.selections[step.id] || {}).reduce((sum, qty) => sum + qty, 0);
            if (step.required && stepTotal < step.minSelections) incompleteSteps.push(step.title);
            if (step.maxSelections != null && step.maxSelections > 0 && stepTotal > step.maxSelections) incompleteSteps.push(`${step.title} (too many items)`);        });
        return {
            isValid: incompleteSteps.length === 0,
            message: incompleteSteps.length > 0 ? `Please complete: ${incompleteSteps.join(', ')}` : ''
        };
    }
    
    calculateDiscount() {
        switch (this.config.pricingMode) {
            case 'fixed':
                return Math.max(0, this.subtotal - (this.config.fixedPrice || 0));
            case 'percentage': {
                const tier = this.getAppliedDiscountTier();
                const pct = tier ? tier.discount * 100 : this.config.discountPercentage;
                return pct > 0 ? this.subtotal * (pct / 100) : 0;
            }
            case 'amount': {
                const tier = this.getAppliedDiscountTier();
                const amt = tier ? tier.discount : (this.config.discountAmount / 100);
                return amt > 0 ? Math.min(amt, this.subtotal) : 0;
            }
            case 'tiered': {
                const tier = this.getAppliedDiscountTier();
                if (!tier) return 0;
                if (tier.percent) return this.subtotal * (tier.percent / 100);
                if (tier.amount) return Math.min(tier.amount, this.subtotal);
                return tier.type === 'fixed' ? tier.discount : this.subtotal * tier.discount;
            }
            default:
                return 0;
        }
    }
    
    getAppliedDiscountTier() {
        if (!this.config.discountTiers || this.config.discountTiers.length === 0) return null;
        let applicableTier = null;
        this.config.discountTiers.forEach(tier => {
            if (tier.minQuantity && this.totalQuantity >= tier.minQuantity) {
                if (!applicableTier || tier.minQuantity > applicableTier.minQuantity) applicableTier = tier;
            } else if (tier.minPrice && this.subtotal >= tier.minPrice) {
                if (!applicableTier || tier.minPrice > applicableTier.minPrice) applicableTier = tier;
            }
        });
        return applicableTier;
    }

    getDiscountCode() {
        if (this.config.pricingMode === 'tiered') {
            const tier = this.getAppliedDiscountTier();
            return tier?.code || null;
        }
        return this.config.discountCode || null;
    }
    
    updateNavigationButtons() {
        const prevBtn = document.getElementById('prevBtn');
        const nextBtn = document.getElementById('nextBtn');
        const step = this.config.steps[this.currentStepIndex];
        const isMultiStep = this.config.steps.length > 1;
        const isFirstStep = this.currentStepIndex === 0;
        const isLastStep = this.currentStepIndex === this.config.steps.length - 1;

        // Hide original nav container always
        const navContainer = prevBtn.closest('.step-navigation');
        if (navContainer) navContainer.style.display = 'none';

        // Desktop summary nav
        const summaryNavButtons = document.getElementById('summaryNavButtons');
        const summaryPrevBtn = document.getElementById('summaryPrevBtn');
        const summaryNextBtn = document.getElementById('summaryNextBtn');
        const addToCartBtn = document.getElementById('addToCartBtn');

        const mobileCheckoutInline = document.getElementById('mobileCheckoutInline');

        // Mobile nav — collapsed only
        const mobileNavRow = document.getElementById('mobileNavRow');
        const mobilePrevBtn = document.getElementById('mobilePrevBtn');
        const mobileNextBtn = document.getElementById('mobileNextBtn');
        const mobileCheckoutBtn = document.getElementById('mobileCheckoutBtn');
        const mobileExpandedNav = document.getElementById('mobileExpandedNav');

        if (isMultiStep) {
            if (mobileCheckoutInline) mobileCheckoutInline.style.display = 'none';
            if (summaryNavButtons) summaryNavButtons.style.display = 'flex';
            if (mobileNavRow) mobileNavRow.style.display = 'flex';

            const showPrev = !isFirstStep;
            if (summaryPrevBtn) summaryPrevBtn.style.display = showPrev ? 'block' : 'none';
            if (mobilePrevBtn) mobilePrevBtn.style.display = showPrev ? 'block' : 'none';
            if (mobileExpandedNav) mobileExpandedNav.style.display = 'flex';

            if (isLastStep) {
                if (summaryNextBtn) summaryNextBtn.style.display = 'none';
                if (addToCartBtn) addToCartBtn.style.display = 'block';
                if (mobileNextBtn) mobileNextBtn.style.display = 'none';
                if (mobileCheckoutBtn) mobileCheckoutBtn.style.display = '';
            } else {
                if (summaryNextBtn) summaryNextBtn.style.display = 'block';
                if (addToCartBtn) addToCartBtn.style.display = 'none';
                if (mobileNextBtn) mobileNextBtn.style.display = 'block';
                if (mobileCheckoutBtn) mobileCheckoutBtn.style.display = 'none';
            }
            this._singleStepMode = false;
        } else {
            if (mobileCheckoutInline) mobileCheckoutInline.style.display = '';
            if (mobileCheckoutBtn) mobileCheckoutBtn.style.display = 'none';
            if (mobileExpandedNav) mobileExpandedNav.style.display = 'none';
            if (summaryNavButtons) summaryNavButtons.style.display = 'block';
            if (addToCartBtn) addToCartBtn.style.display = 'block';
            if (summaryPrevBtn) summaryPrevBtn.style.display = 'none';
            if (summaryNextBtn) summaryNextBtn.style.display = 'none';
            if (mobileNavRow) mobileNavRow.style.display = 'none';
            if (mobileCheckoutBtn) mobileCheckoutBtn.style.display = '';
            if (mobileExpandedNav) mobileExpandedNav.style.display = 'flex';
            this._singleStepMode = true;
        }

        // Validation
        const stepTotal = Object.values(this.selections[step.id] || {}).reduce((sum, qty) => sum + qty, 0);
        let isValid = true;

        if (step.minSelections > 0 && stepTotal < step.minSelections) isValid = false;
        if (step.maxSelections != null && step.maxSelections > 0 && stepTotal > step.maxSelections) isValid = false;
        if (step.required && stepTotal === 0) isValid = false;

        if (summaryNextBtn) summaryNextBtn.disabled = !isValid;
        if (mobileNextBtn) mobileNextBtn.disabled = !isValid;
        if (nextBtn) nextBtn.disabled = !isValid;
    }
    
    nextStep() {
        if (this.currentStepIndex < this.config.steps.length - 1) {
            this.currentStepIndex++;
            this.renderProgressSteps();
            this.renderCurrentStep();
            this.updateSummary();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // ADA: Move focus to the new step title
            const stepTitle = document.getElementById('stepTitle');
            if (stepTitle) stepTitle.focus();
        }
    }
    
    prevStep() {
        if (this.currentStepIndex > 0) {
            this.currentStepIndex--;
            this.renderProgressSteps();
            this.renderCurrentStep();
            this.updateSummary();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // ADA: Move focus to the new step title
            const stepTitle = document.getElementById('stepTitle');
            if (stepTitle) stepTitle.focus();
        }
    }
    
    
    async addToCart() {
        const validation = this.validateAllRequiredSteps();
        if (!validation.isValid) {
            this.announce(validation.message);
            return;
        }

        const bundleId = 'bundle_' + Date.now();
        const items = [];

        const discountTier = this.getAppliedDiscountTier();
        let actualDiscountPercent = 0;
        if (this.config.pricingMode === 'percentage') {
            actualDiscountPercent = (discountTier && discountTier.discount)
                ? discountTier.discount * 100
                : this.config.discountPercentage;
        }

        const firstStep = this.config.steps[0];
        const firstStepProducts = this.productData[firstStep.productHandle];
        const bundleImage = (this.config.bundleImage && !this.config.bundleImage.includes('no-image'))
            ? this.config.bundleImage
            : firstStepProducts?.featuredImage || firstStepProducts?.variants?.[0]?.image || '';

        const selectedItems = [];
        this.config.steps.forEach((step, stepIndex) => {
            const products = this.productData[step.productHandle];
            if (!products || !this.selections[step.id]) return;
            Object.entries(this.selections[step.id]).forEach(([variantId, quantity]) => {
                const variant = products.variants.find(v => v.variantId == variantId);
                if (!variant) return;
                selectedItems.push({ step, stepIndex, variant, variantId, quantity });
            });
        });

        if (selectedItems.length === 0) {
            this.announce('No items selected. Please add items to your bundle.');
            return;
        }

        selectedItems.forEach((item) => {
            items.push({
                id: parseInt(item.variantId),
                quantity: parseInt(item.quantity),
                properties: {
                    '_bundle_id': bundleId,
                    '_bundle_name': this.config.bundleName,
                    '_bundle_image': bundleImage,
                    '_pricing_mode': this.config.pricingMode,
                    '_original_price': item.variant.price.toFixed(2),
                    '_bundle_subtotal': this.subtotal.toFixed(2),
                    '_bundle_target_price': this.config.pricingMode === 'fixed' ? this.config.fixedPrice.toFixed(2) : '',
                    '_discount_percent': this.config.pricingMode === 'percentage' ? actualDiscountPercent.toString() : '',
                    '_discount_amount': this.config.pricingMode === 'amount' ? (this.config.discountAmount / 100).toFixed(2) : '',
                    '_bundle_total': this.total.toFixed(2)
                }
            });
        });

        this.announce('Adding bundle to cart, please wait.');

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to add to cart: ${response.status} - ${errorText}`);
            }

            await response.json();

            const code = this.getDiscountCode();
            if (code) {
                window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
            } else {
                window.location.href = '/cart';
            }

        } catch (error) {
            console.error('Error adding to cart:', error);
            try {
                const simpleItems = items.map(item => ({ id: item.id, quantity: item.quantity }));
                const retryResponse = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: simpleItems })
                });
                if (retryResponse.ok) {
                    window.location.href = '/cart';
                    return;
                }
            } catch (retryError) {
                console.error('Retry also failed:', retryError);
            }
            this.announce('There was an error adding your bundle to the cart. Please try again.');
        }
    }
    /*
    async addToCart() {
        const validation = this.validateAllRequiredSteps();
        if (!validation.isValid) {
            this.announce(validation.message);
            return;
        }

        const bundleId = 'bundle_' + Date.now();

        const discountTier = this.getAppliedDiscountTier();
        let actualDiscountPercent = 0;
        if (this.config.pricingMode === 'percentage') {
            actualDiscountPercent = (discountTier && discountTier.discount)
                ? discountTier.discount * 100
                : this.config.discountPercentage;
        }

        const selectedItems = [];
        this.config.steps.forEach((step, stepIndex) => {
            const products = this.productData[step.productHandle];
            if (!products || !this.selections[step.id]) return;
            Object.entries(this.selections[step.id]).forEach(([variantId, quantity]) => {
                const variant = products.variants.find(v => v.variantId == variantId);
                if (!variant) return;
                selectedItems.push({ step, stepIndex, variant, variantId, quantity });
            });
        });

        if (selectedItems.length === 0) {
            this.announce('No items selected. Please add items to your bundle.');
            return;
        }

        // Giftship bundle properties — one entry per unit, not per line
        const gsBundleIds = [];
        const gsBundleContents = [];
        const gsBundlePrices = [];
        const bundleVariantIds = [];

        selectedItems.forEach(item => {
            for (let i = 0; i < item.quantity; i++) {
                gsBundleIds.push(String(item.variantId));
                gsBundleContents.push(item.variant.title);
                gsBundlePrices.push(String(Math.round(item.variant.price * 100)));
                bundleVariantIds.push(String(item.variantId));
            }
        });

        // First step product image for cart display
        const firstStep = this.config.steps[0];
        const firstStepProducts = this.productData[firstStep.productHandle];
        const bundleImage = (this.config.bundleImage && !this.config.bundleImage.includes('no-image'))
            ? this.config.bundleImage
            : firstStepProducts?.featuredImage || firstStepProducts?.variants?.[0]?.image || '';
        const placeholderVariantId = parseInt(this.config.bundleVariantId);

        if (!placeholderVariantId) {
            console.error('Bundle placeholder variant ID not configured');
            this.announce('Configuration error. Please contact support.');
            return;
        }

        this.announce('Adding bundle to cart, please wait.');

        try {
            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: [{
                        id: placeholderVariantId,
                        quantity: 1,
                        properties: {
                            '_bundle_id': bundleId,
                            '_bundle_name': this.config.bundleName,
                            '_bundle_image': bundleImage,
                            '_pricing_mode': this.config.pricingMode,
                            '_bundle_total': this.total.toFixed(2),
                            '_discount_percent': this.config.pricingMode === 'percentage' ? actualDiscountPercent.toString() : '',
                            '_discount_amount': this.config.pricingMode === 'amount' ? (this.config.discountAmount / 100).toFixed(2) : '',
                            '_bundle_variant_ids': bundleVariantIds,
                            '_bundle_variant_names': gsBundleContents,
                            '_gs_bundle_ids': gsBundleIds,
                            '_gs_bundle_contents': gsBundleContents,
                            '_gs_bundle_prices': gsBundlePrices
                        }
                    }]
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to add to cart: ${response.status} - ${errorText}`);
            }

            await response.json();

            const code = this.getDiscountCode();
            if (code) {
                window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
            } else {
                window.location.href = '/cart';
            }

        } catch (error) {
            console.error('Error adding to cart:', error);
            this.announce('There was an error adding your bundle to the cart. Please try again.');
        }
    }*/
    
    renderFilters(step, products) {
        const filtersContainer = document.getElementById('bundleFilters');
        const buttonsContainer = document.getElementById('bundleFilterButtons');
        const searchInput = document.getElementById('bundleSearchInput');

        if (!filtersContainer || !products || !products.variants || !this.config.showFilters) {
            if (filtersContainer) filtersContainer.style.display = 'none';
            return;
        }

        // Check if any variants have categories or if filter categories are configured
        const hasCategories = this.config.filterCategories && this.config.filterCategories.length > 0;
        const hasVariants = products.variants.length > 1;

        if (!hasVariants) {
            filtersContainer.style.display = 'none';
            return;
        }

        filtersContainer.style.display = 'block';

        // Build filter buttons
        buttonsContainer.innerHTML = '<button class="bundle-filter-btn active" data-filter="all">All</button>';

        if (hasCategories) {
            this.config.filterCategories.forEach(category => {
                const trimmed = category.trim();
                if (!trimmed) return;
                const btn = document.createElement('button');
                btn.className = 'bundle-filter-btn';
                btn.dataset.filter = trimmed;
                btn.textContent = trimmed;
                buttonsContainer.appendChild(btn);
            });
        }

        // Clear previous listeners by replacing input
        const newSearchInput = searchInput.cloneNode(true);
        searchInput.parentNode.replaceChild(newSearchInput, searchInput);

        // Search handler
        newSearchInput.addEventListener('input', (e) => {
            this._currentSearch = e.target.value.toLowerCase().trim();
            this.applyFilters(products);
        });

        // Filter button handler
        buttonsContainer.addEventListener('click', (e) => {
            const btn = e.target.closest('.bundle-filter-btn');
            if (!btn) return;

            buttonsContainer.querySelectorAll('.bundle-filter-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');

            this._currentFilter = btn.dataset.filter;
            this.applyFilters(products);
        });

        // Reset filter state for new step
        this._currentFilter = 'all';
        this._currentSearch = '';
        newSearchInput.value = '';
    }

    applyFilters(products) {
        const grid = document.getElementById('productGrid');
        const cards = grid.querySelectorAll('.product-card');
        const search = this._currentSearch || '';
        const filter = this._currentFilter || 'all';

        const visibleVariants = this.config.hideOutOfStock
            ? products.variants.filter(v => v.available)
            : products.variants;

        visibleVariants.forEach((variant, index) => {

            const card = cards[index];
            if (!card) return;

            const matchesSearch = search === '' ||
                variant.title.toLowerCase().includes(search) ||
                (variant.description && variant.description.toLowerCase().includes(search));

            const matchesFilter = filter === 'all' ||
            (variant.category && (
                Array.isArray(variant.category)
                    ? variant.category.some(c => 
                        c.split(',').map(s => s.trim().toLowerCase()).includes(filter.toLowerCase())
                    )
                    : String(variant.category).split(',').map(s => s.trim().toLowerCase()).includes(filter.toLowerCase())
            ));
            card.style.display = (matchesSearch && matchesFilter) ? '' : 'none';
        });

        // Announce results for screen readers
        const visibleCount = grid.querySelectorAll('.product-card:not([style*="display: none"])').length;
        this.announce(`${visibleCount} product${visibleCount !== 1 ? 's' : ''} shown.`);

        // Show/hide no results message
        let noResults = document.getElementById('bundleNoResults');
        if (visibleCount === 0) {
            if (!noResults) {
                noResults = document.createElement('div');
                noResults.id = 'bundleNoResults';
                noResults.className = 'bundle-no-results';
                noResults.textContent = 'No products match your search or filter.';
                grid.appendChild(noResults);
            }
            noResults.style.display = '';
        } else {
            if (noResults) noResults.style.display = 'none';
        }
    }

    bindEvents() {
        document.getElementById('prevBtn').addEventListener('click', () => this.prevStep());
        document.getElementById('nextBtn').addEventListener('click', () => this.nextStep());
        // Summary nav buttons (desktop)
        const summaryPrevBtn = document.getElementById('summaryPrevBtn');
        const summaryNextBtn = document.getElementById('summaryNextBtn');
        if (summaryPrevBtn) summaryPrevBtn.addEventListener('click', () => this.prevStep());
        if (summaryNextBtn) summaryNextBtn.addEventListener('click', () => this.nextStep());
        const mobileNextBtnExpanded = document.getElementById('mobileNextBtnExpanded');
        if (mobileNextBtnExpanded) mobileNextBtnExpanded.addEventListener('click', () => this.nextStep());

        // Mobile nav buttons
        const mobilePrevBtn = document.getElementById('mobilePrevBtn');
        const mobileNextBtn = document.getElementById('mobileNextBtn');
        if (mobilePrevBtn) mobilePrevBtn.addEventListener('click', () => this.prevStep());
        if (mobileNextBtn) mobileNextBtn.addEventListener('click', () => this.nextStep());
        document.getElementById('addToCartBtn').addEventListener('click', () => this.addToCart());

        const mobileCheckoutInline = document.getElementById('mobileCheckoutInline');
        if (mobileCheckoutInline) mobileCheckoutInline.addEventListener('click', () => this.addToCart());
        
        document.getElementById('modalClose').addEventListener('click', () => this.closeProductModal());
        document.getElementById('modalOverlay').addEventListener('click', () => this.closeProductModal());
        
        document.getElementById('modalAddBtn').addEventListener('click', () => {
            const modal = document.getElementById('productModal');
            const variantId = parseInt(modal.dataset.variantId);
            const stepId = parseInt(modal.dataset.stepId);

            const step = this.config.steps.find(s => s.id === stepId);
            const products = step ? this.productData[step.productHandle] : null;
            const variant = products?.variants.find(v => v.variantId == variantId);

            if (!variant?.available) {
                this.closeProductModal();
                return;
            }

            this.updateQuantity(stepId, variantId, 1);
            this.closeProductModal();
        });
        
        const modalCloseBtn = document.getElementById('modalCloseBtn');
        if (modalCloseBtn) {
            modalCloseBtn.addEventListener('click', () => this.closeProductModal());
        }
        
        // ADA: Escape key closes modal (handler is separate from focus trap)
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.closeProductModal();
        });
        
        document.addEventListener('click', (e) => {
            if (e.target.hasAttribute('data-mobile-add')) {
                if (e.target.disabled) return;
                const variantId = parseInt(e.target.dataset.variantId);
                const step = this.config.steps[this.currentStepIndex];
                this.updateQuantity(step.id, variantId, 1);
            }
            
            if (e.target.classList.contains('product-qty-btn')) {
                const controls = e.target.closest('.mobile-quantity-controls');
                const variantId = parseInt(controls.dataset.variantId);
                const step = this.config.steps[this.currentStepIndex];
                const action = e.target.dataset.action;
                this.updateQuantity(step.id, variantId, action === 'plus' ? 1 : -1);
            }
        });
    }
    
    initializeMobileSummary() {
        const mobileSummary = document.getElementById('mobileSummary');
        if (!mobileSummary) return;
        
        const viewBoxBtn = document.getElementById('mobileViewBoxBtn');
        const hideBoxBtn = document.getElementById('mobileHideBoxBtn');
        const checkoutBtn = document.getElementById('mobileCheckoutBtn');
        const checkoutBtnFull = document.getElementById('mobileCheckoutBtnFull');
        
        if (viewBoxBtn) {
            viewBoxBtn.addEventListener('click', () => {
                mobileSummary.classList.remove('collapsed');
                mobileSummary.classList.add('expanded');
                viewBoxBtn.setAttribute('aria-expanded', 'true');
                this.updateMobileSummaryItems();
            });
        }
        if (hideBoxBtn) {
            hideBoxBtn.addEventListener('click', () => {
                mobileSummary.classList.remove('expanded');
                mobileSummary.classList.add('collapsed');
                if (viewBoxBtn) viewBoxBtn.setAttribute('aria-expanded', 'false');
                if (viewBoxBtn) viewBoxBtn.focus();
            });
        }
        if (checkoutBtn) checkoutBtn.addEventListener('click', () => this.addToCart());
        if (checkoutBtnFull) checkoutBtnFull.addEventListener('click', () => this.addToCart());
    }
    
    updateMobileSummary() {
        const progressLabel = document.getElementById('mobileProgressLabel');
        if (!progressLabel) return;
        
        const progressFill = document.getElementById('mobileProgressFill');
        const progressLabelExpanded = document.getElementById('mobileProgressLabelExpanded');
        const progressFillExpanded = document.getElementById('mobileProgressFillExpanded');
        const progressContainer = document.getElementById('mobileProgressContainer');
        const checkoutBtnFull = document.getElementById('mobileCheckoutBtnFull');
        const checkoutBtn = document.getElementById('mobileCheckoutBtn');
        
        const maxItems = this.config.steps.reduce((sum, step) => sum + (step.maxSelections || 12), 0) || 12;
        const progress = Math.min((this.totalQuantity / maxItems) * 100, 100);
        const isFull = this.totalQuantity >= maxItems;
        
        const progressText = `${this.totalQuantity}/${maxItems} Items`;
        if (progressLabel) progressLabel.textContent = progressText;
        if (progressLabelExpanded) progressLabelExpanded.textContent = progressText;
        if (progressFill) progressFill.style.width = `${progress}%`;
        if (progressFillExpanded) progressFillExpanded.style.width = `${progress}%`;

        // Update "View Box" / "Review Box" toggle text based on full state
        const mobileToggleText = document.getElementById('mobileToggleText');
        if (mobileToggleText) {
            mobileToggleText.textContent = isFull ? 'Review Box' : 'View Box';
        }

        // ADA: Update mobile progress bar ARIA values
        const mobileBarWrapper = document.getElementById('mobileProgressBarWrapper');
        if (mobileBarWrapper) {
            mobileBarWrapper.setAttribute('aria-valuenow', this.totalQuantity);
            mobileBarWrapper.setAttribute('aria-valuemax', maxItems);
            mobileBarWrapper.setAttribute('aria-valuetext',
                `${this.totalQuantity} of ${maxItems} items selected`);
        }
        const mobileExpandedBarWrapper = document.getElementById('mobileProgressExpandedBarWrapper');
        if (mobileExpandedBarWrapper) {
            mobileExpandedBarWrapper.setAttribute('aria-valuenow', this.totalQuantity);
            mobileExpandedBarWrapper.setAttribute('aria-valuemax', maxItems);
            mobileExpandedBarWrapper.setAttribute('aria-valuetext',
                `${this.totalQuantity} of ${maxItems} items selected`);
        }
        
        if (progressContainer && checkoutBtnFull) {
            if (!this.config.showProgressBar) {
                progressContainer.style.display = 'none';
                // Show checkout button on last step even without progress bar
                const isLastStep = this.currentStepIndex === this.config.steps.length - 1;
                const isSingleStep = this.config.steps.length === 1;
                checkoutBtnFull.style.display = (isLastStep || isSingleStep) ? 'block' : 'none';
            } else {
                progressContainer.style.display = isFull ? 'none' : 'block';
                checkoutBtnFull.style.display = isFull ? 'block' : 'none';
            }
        }
        // In single-step mode, keep expanded checkout inline with hide box
        if (this._singleStepMode) {
            const inlineBtn = document.getElementById('mobileCheckoutInline');
            const expandedNav = document.getElementById('mobileExpandedNav');
            if (checkoutBtn) checkoutBtn.style.display = 'none';
            if (expandedNav) expandedNav.style.display = 'none';
            if (inlineBtn) inlineBtn.style.display = '';
        }
        if (!this.config.showProgressBar) {
            const expandedContainer = document.getElementById('mobileProgressExpandedContainer');
            if (expandedContainer) expandedContainer.style.display = 'none';
        }
        // Center view box when nothing else is showing on that row
        const collapsedContent = document.querySelector('.mobile-summary-collapsed-content');
        if (collapsedContent) {
            const hasVisibleChild = collapsedContent.querySelector(':scope > *:not([style*="display: none"])');
            collapsedContent.style.display = hasVisibleChild ? '' : 'none';
        }
        
        if (checkoutBtn) {
            const allRequiredComplete = this.validateAllRequiredSteps();
            const isDisabled = this.totalQuantity === 0 || !allRequiredComplete.isValid;
            checkoutBtn.disabled = isDisabled;

            // Also sync inline checkout button
            const inlineBtn = document.getElementById('mobileCheckoutInline');
            if (inlineBtn) inlineBtn.disabled = isDisabled;
        }
        
        /*const mobileBundleQuantity = document.getElementById('mobileBundleQuantity');*/
        const mobileTotal = document.getElementById('mobileTotal');
        /*if (mobileBundleQuantity) mobileBundleQuantity.textContent = `x${this.totalQuantity}`;*/
        if (mobileTotal) mobileTotal.textContent = `$${this.total.toFixed(2)}`;

        const mobileSubtotalRow = document.getElementById('mobileSubtotalRow');
        const mobileDiscountRow = document.getElementById('mobileDiscountRow');
        const mobileSubtotal = document.getElementById('mobileSubtotal');
        const mobileDiscountLabel = document.getElementById('mobileDiscountLabel');
        const mobileDiscountAmount = document.getElementById('mobileDiscountAmount');

        if (mobileSubtotalRow && mobileDiscountRow) {
            if (this.config.pricingMode !== 'fixed' && this.discount > 0) {
                mobileSubtotalRow.style.display = 'flex';
                mobileDiscountRow.style.display = 'flex';
                if (mobileSubtotal) mobileSubtotal.textContent = `$${this.subtotal.toFixed(2)}`;
                const discountTier = this.getAppliedDiscountTier();
                if (mobileDiscountLabel) mobileDiscountLabel.textContent = discountTier?.label || 'Discount';
                if (mobileDiscountAmount) mobileDiscountAmount.textContent = `-$${this.discount.toFixed(2)}`;
            } else {
                mobileSubtotalRow.style.display = 'none';
                mobileDiscountRow.style.display = 'none';
            }
        }

        const mobileSummary = document.getElementById('mobileSummary');
        if (mobileSummary && mobileSummary.classList.contains('expanded')) {
            this.updateMobileSummaryItems();
        }
    }
    
    updateMobileSummaryItems() {
        const container = document.getElementById('mobileSummaryItems');
        if (!container) return;
        
        container.innerHTML = '';
        
        this.config.steps.forEach(step => {
            const products = this.productData[step.productHandle];
            if (!products || !this.selections[step.id]) return;
            
            Object.entries(this.selections[step.id]).forEach(([variantId, quantity]) => {
                if (quantity === 0) return;
                const variant = products.variants.find(v => v.variantId == variantId);
                if (!variant) return;
                
                const itemDiv = document.createElement('div');
                itemDiv.className = 'mobile-summary-item';
                itemDiv.innerHTML = `
                    <div class="mobile-summary-item-image">
                        <img src="${variant.image}" alt="${variant.title}">
                    </div>
                    <div class="mobile-summary-item-title">${variant.title}</div>
                    <div class="mobile-summary-item-quantity">x${quantity}</div>
                    ${!this.config.hideVariantPricesMobile ? `
                        <div class="mobile-summary-item-price">${(variant.price * quantity).toFixed(2)}</div>
                    ` : ''}
                `;
                container.appendChild(itemDiv);
            });
        });
    }
    };
}

if (typeof BundleBuilder === 'undefined') {
    var BundleBuilder = window.BundleBuilder;
}

let domReady = false;
let productsReady = false;

document.addEventListener('DOMContentLoaded', () => {
    domReady = true;
    tryInitialize();
});

window.addEventListener('bundleProductsLoaded', () => {
    productsReady = true;
    tryInitialize();
});

setTimeout(() => {
    if (typeof SHOPIFY_PRODUCT_DATA !== 'undefined' && Object.keys(SHOPIFY_PRODUCT_DATA).length > 0) {
        productsReady = true;
        tryInitialize();
    }
}, 100);

function tryInitialize() {
    if (!domReady || !productsReady) return;
    if (window.bundleBuilderInstance && window.bundleBuilderInstance instanceof window.BundleBuilder) return;
    
    if (typeof BUNDLE_CONFIG === 'undefined') {
        console.error('BUNDLE_CONFIG not found.');
        const el = document.getElementById('bundleBuilder');
        if (el) {
            el.innerHTML = `
                <div style="padding: 3rem; text-align: center; background: #fff3cd; border: 2px solid #ffc107; border-radius: 8px; margin: 2rem;" role="alert">
                    <h2 style="color: #856404; margin-bottom: 1rem;">Configuration Missing</h2>
                    <p style="color: #856404;">BUNDLE_CONFIG not found. Please ensure bundle-config.js is loaded.</p>
                </div>
            `;
        }
        return;
    }
    
    const productData = typeof SHOPIFY_PRODUCT_DATA !== 'undefined'
        ? SHOPIFY_PRODUCT_DATA
        : (typeof EXAMPLE_PRODUCT_DATA !== 'undefined' ? EXAMPLE_PRODUCT_DATA : {});
    
    try {
        const builder = new window.BundleBuilder(BUNDLE_CONFIG, productData);
        window.bundleBuilderInstance = builder;
        window.bundleBuilder = builder;
    } catch (error) {
        console.error('Error initializing Bundle Builder:', error);
        const el = document.getElementById('bundleBuilder');
        if (el) {
            el.innerHTML = `
                <div style="padding: 3rem; text-align: center; background: #f8d7da; border: 2px solid #dc3545; border-radius: 8px; margin: 2rem;" role="alert">
                    <h2 style="color: #721c24; margin-bottom: 1rem;">Initialization Error</h2>
                    <p style="color: #721c24; margin-bottom: 1rem;">Bundle Builder failed to initialize.</p>
                    <p style="color: #721c24; font-family: monospace; font-size: 0.875rem;">${error.message}</p>
                </div>
            `;
        }
    }
}
